import _slicedToArray from '@babel/runtime/helpers/slicedToArray';
import { useRef, useState, useEffect, useCallback } from 'react';

/**
 * Used for managing states in Cogs components for the cases that we support
 * both controlled and uncontrolled states. The implementation is inspired from
 * https://github.com/mui/material-ui/blob/25bf580dd02837d4d909bac84f0552576ed13008/packages/mui-utils/src/useControlled/useControlled.js
 */
var useControllableState = function useControllableState(_ref) {
  var controlledValue = _ref.controlledValue,
    defaultProp = _ref.defaultValue,
    componentName = _ref.componentName,
    _ref$stateName = _ref.stateName,
    stateName = _ref$stateName === undefined ? 'value' : _ref$stateName;
  // isControlled is ignored in the hook dependency lists as it should never change.
  var isControlled = useRef(controlledValue !== undefined);
  var _useState = useState(defaultProp),
    _useState2 = _slicedToArray(_useState, 2),
    valueState = _useState2[0],
    setValue = _useState2[1];
  // The component shouldn't change from controlled to uncontrolled. If it
  // starts in controlled mode, we keep returning the `controlledValue`.
  var value = isControlled.current ? controlledValue : valueState;
  /* eslint-disable react-hooks/rules-of-hooks, no-console, react-hooks/exhaustive-deps */
  if (process.env.NODE_ENV !== 'production') {
    useEffect(function () {
      if (isControlled.current !== (controlledValue !== undefined)) {
        console.error(["Cogs: A component is changing the ".concat(isControlled.current ? '' : 'un', "controlled ").concat(stateName, " state of ").concat(componentName, " to be ").concat(isControlled.current ? 'un' : '', "controlled."), 'Elements should not switch from uncontrolled to controlled (or vice versa).', "Decide between using a controlled or uncontrolled ".concat(componentName, " ") + 'element for the lifetime of the component.', "The nature of the state is determined during the first render. It's considered controlled if the value is not `undefined`.", 'More info: https://fb.me/react-controlled-components'].join('\n'));
      }
    }, [stateName, componentName, controlledValue]);
    var _useRef = useRef(defaultProp),
      defaultValue = _useRef.current;
    useEffect(function () {
      // Object.is() is not equivalent to the === operator.
      // See https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/is for more details.
      if (!isControlled.current && !Object.is(defaultValue, defaultProp)) {
        console.error(["Cogs: A component is changing the default ".concat(stateName, " state of an uncontrolled ").concat(componentName, " after being initialized. "), "To suppress this warning opt to use a controlled ".concat(componentName, ".")].join('\n'));
      }
    }, [JSON.stringify(defaultProp)]);
  }
  /* eslint-enable react-hooks/rules-of-hooks, no-console, react-hooks/exhaustive-deps */
  var setValueIfUncontrolled = useCallback(function (newValue) {
    if (!isControlled.current) {
      setValue(newValue);
    }
  }, []);
  return [value, setValueIfUncontrolled];
};

export { useControllableState };
